import { useEffect, useMemo, useState } from "react";
import { Compass, RotateCcw, Send } from "lucide-react";
import { toast } from "react-toastify";
import {
  getApprovedBusinessesForUserOverrides,
  getBusinessUsers,
} from "../../../api/users";
import {
  forceAdminProductGuidanceReplay,
  getAdminProductGuidanceStates,
  resetAdminProductGuidanceState,
} from "../../../features/productGuidance/productGuidanceAdminApi";
import { TOUR_REGISTRY } from "../../../features/productGuidance/tourRegistry";

const EXPERIENCES = Object.values(TOUR_REGISTRY).map(definition => ({
  title: definition.title || definition.experienceKey,
  experienceType: definition.experienceType,
  experienceKey: definition.experienceKey,
  version: definition.version,
}));

const identityOf = experience =>
  `${experience.experienceType}:${experience.experienceKey}:${experience.version}`;

const isEligibleProductGuidanceUser = user =>
  user?.status === "Active" && user?.isDeleted !== true;

export default function ProductGuidanceAdminPage() {
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [businessId, setBusinessId] = useState("");
  const [userId, setUserId] = useState("");
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mutationKey, setMutationKey] = useState("");

  useEffect(() => {
    getApprovedBusinessesForUserOverrides()
      .then(setBusinesses)
      .catch(() => toast.error("Failed to load businesses."));
  }, []);

  useEffect(() => {
    setUserId("");
    setStates([]);
    if (!businessId) {
      setUsers([]);
      return;
    }

    let cancelled = false;
    getBusinessUsers(businessId)
      .then(items => {
        if (!cancelled) setUsers(items.filter(isEligibleProductGuidanceUser));
      })
      .catch(() => {
        if (!cancelled) {
          setUsers([]);
          toast.error("Failed to load users for this business.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (!businessId || !userId) {
      setStates([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getAdminProductGuidanceStates(businessId, userId)
      .then(items => {
        if (!cancelled) setStates(items);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load Product Guidance state.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, userId]);

  const statesByIdentity = useMemo(
    () => Object.fromEntries(states.map(state => [identityOf(state), state])),
    [states]
  );

  const mutationPayload = experience => ({
    experienceType: experience.experienceType,
    experienceKey: experience.experienceKey,
    version: experience.version,
  });

  async function resetExperience(experience) {
    const identity = identityOf(experience);
    setMutationKey(`reset:${identity}`);
    try {
      await resetAdminProductGuidanceState(
        businessId,
        userId,
        mutationPayload(experience)
      );
      setStates(current => current.filter(state => identityOf(state) !== identity));
      toast.success("Experience reset to Not Started.");
    } catch {
      toast.error("Failed to reset this experience.");
    } finally {
      setMutationKey("");
    }
  }

  async function forceReplay(experience) {
    const identity = identityOf(experience);
    setMutationKey(`force:${identity}`);
    try {
      const state = await forceAdminProductGuidanceReplay(
        businessId,
        userId,
        mutationPayload(experience)
      );
      if (state) {
        setStates(current => [
          ...current.filter(item => identityOf(item) !== identity),
          state,
        ]);
      }
      toast.success("One forced replay is pending for the selected user.");
    } catch {
      toast.error("Failed to request a forced replay.");
    } finally {
      setMutationKey("");
    }
  }

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
          <Compass className="h-5 w-5 text-emerald-600" />
          Product Guidance Administration
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Inspect, reset, or request one replay for a selected business user.
        </p>
      </header>

      <section className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-2">
        <label className="space-y-1 text-sm font-medium text-slate-700">
          <span>Business</span>
          <select
            aria-label="Business"
            value={businessId}
            onChange={event => setBusinessId(event.target.value)}
            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3"
          >
            <option value="">Select business</option>
            {businesses.map(business => (
              <option key={business.id} value={business.id}>
                {business.name}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-sm font-medium text-slate-700">
          <span>User</span>
          <select
            aria-label="User"
            value={userId}
            disabled={!businessId}
            onChange={event => setUserId(event.target.value)}
            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 disabled:bg-slate-50"
          >
            <option value="">{businessId ? "Select user" : "Select business first"}</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.name || user.email || user.id}
              </option>
            ))}
          </select>
        </label>
      </section>

      {!userId ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
          Select a business and user to inspect Product Guidance state.
        </div>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">
            Known experiences {loading && <span className="text-slate-400">— Loading…</span>}
          </div>
          <div className="divide-y divide-slate-100">
            {EXPERIENCES.map(experience => {
              const identity = identityOf(experience);
              const state = statesByIdentity[identity];
              const pending = state?.forceReplayPending === true;
              return (
                <div key={identity} className="grid gap-3 px-4 py-4 lg:grid-cols-[1fr_auto] lg:items-center">
                  <div>
                    <h2 className="font-semibold text-slate-900">{experience.title}</h2>
                    <p className="mt-1 text-xs text-slate-500">
                      {experience.experienceKey} · {experience.experienceType} v{experience.version}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                        {state?.status || "Not Started"}
                      </span>
                      {pending && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-800">
                          Force replay pending
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={Boolean(mutationKey)}
                      onClick={() => resetExperience(experience)}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
                      aria-label={`Reset ${experience.title}`}
                    >
                      <RotateCcw className="h-4 w-4" /> Reset
                    </button>
                    <button
                      type="button"
                      disabled={Boolean(mutationKey) || pending}
                      onClick={() => forceReplay(experience)}
                      className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      aria-label={`Force replay ${experience.title}`}
                    >
                      <Send className="h-4 w-4" /> Force Replay
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
